// The live look's index — what `tools/meta-pull.mjs` writes beside the app as `live/index.json` (T-282, D-362) —
// as ONE shape and ONE parser (T-286; both apps carried a copy, T-284's note). The FETCH stays per app: the web
// resolves against `document.baseURI` (hash routing), the phone against its origin + `EXPO_BASE_URL` (history
// routing) and a device has no base at all; what they fetch and how they read it is the same.
//
// Nothing here touches a network or a Meta host. The index carries no ids and no secrets: an opaque account key,
// the account's name, the dataset file name, when it was pulled and how fresh it is.

/** One pulled account as `live/index.json` lists it. */
export interface LiveIndexEntry {
  /** The opaque per-machine account key — never the numeric ad account id (HARD RULE 2). */
  key: string;
  name: string;
  /** `<key>.live.json` — one path segment, never a traversal (`LIVE_FILE_NAME`). */
  file: string;
  pulledAt: string;
  dataAsOf: string;
  currency?: string;
  timezone?: string;
  historyDays?: number;
}

export interface LiveIndex {
  accounts: LiveIndexEntry[];
}

export const LIVE_FOLDER = 'live';
export const LIVE_INDEX_FILE = 'index.json';

/** A dataset file name as the pull writes it — one path segment, never a traversal. */
export const LIVE_FILE_NAME = /^[A-Za-z0-9_-]+\.live\.json$/;

/** Thrown when there is nothing to show; the message says what to run (it reaches a designed state, not a crash). */
export class LiveDatasetError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LiveDatasetError';
  }
}

export const NO_LIVE_DATASET_HINT =
  'No live dataset is beside this app. Pull one first: node tools/meta-pull.mjs --preview (docs/architecture/LIVE_LOOK.md).';

/** The index, validated to the shape the pull writes; an entry that is not shaped like one is left out. */
export function parseLiveIndex(raw: unknown): LiveIndex {
  const accounts =
    raw && typeof raw === 'object' && Array.isArray((raw as { accounts?: unknown }).accounts)
      ? ((raw as { accounts: unknown[] }).accounts as unknown[])
      : [];
  const out: LiveIndexEntry[] = [];
  for (const a of accounts) {
    if (!a || typeof a !== 'object') continue;
    const e = a as Record<string, unknown>;
    if (typeof e.key !== 'string' || typeof e.file !== 'string' || !LIVE_FILE_NAME.test(e.file))
      continue;
    out.push({
      key: e.key,
      name: typeof e.name === 'string' ? e.name : e.key,
      file: e.file,
      pulledAt: typeof e.pulledAt === 'string' ? e.pulledAt : '',
      dataAsOf: typeof e.dataAsOf === 'string' ? e.dataAsOf : '',
      ...(typeof e.currency === 'string' ? { currency: e.currency } : {}),
      ...(typeof e.timezone === 'string' ? { timezone: e.timezone } : {}),
      ...(typeof e.historyDays === 'number' ? { historyDays: e.historyDays } : {}),
    });
  }
  return { accounts: out };
}

/** The entry to serve: the one whose key is `accountKey`, else the first; `null` when the index is empty. */
export function pickLiveEntry(
  index: LiveIndex,
  accountKey: string | null | undefined,
): LiveIndexEntry | null {
  return (
    (accountKey ? index.accounts.find((a) => a.key === accountKey) : undefined) ??
    index.accounts[0] ??
    null
  );
}

/** `live/index.json` relative to the app's base (a URL string ending in `/`). */
export function liveIndexUrl(base: string): string {
  return new URL(`${LIVE_FOLDER}/${LIVE_INDEX_FILE}`, base).toString();
}

/** The dataset file of an entry relative to the app's base. */
export function liveFileUrl(base: string, entry: Pick<LiveIndexEntry, 'file'>): string {
  return new URL(`${LIVE_FOLDER}/${entry.file}`, base).toString();
}
